import { Capacitor } from '@capacitor/core';
import { Sidebar } from '@supply-mover-2-0/web-ui';
import React, { useCallback, useEffect } from 'react';
import { useKeyboardInset } from '../hooks/useKeyboardInset';

type PortalSidebarProps = Omit<
	React.ComponentProps<typeof Sidebar>,
	| 'panelClassName'
	| 'dividerClassName'
	| 'closeButtonClassName'
	| 'resizeHandleClassName'
>;

/**
 * The shared Sidebar painted in the storefront's brand tokens.
 *
 * The shared component styles itself from the CRM theme, which knows nothing
 * about a tenant's brand: against a branded page its panel read as a
 * different product (PD-15016), and once the panel was fixed its chrome —
 * the close (X) button, the header divider and the resize grip — still came
 * out in three unrelated CRM grays on a brand-tinted dark panel (PD-15974).
 * Every storefront sidebar goes through this wrapper so the overrides live
 * in one place:
 *
 *   - panel: `bg-brand-surface`, the card surface.
 *   - divider: `brand-border`, the same edge every card and input on the
 *     page already carries, so the panel's seams match the containers
 *     around it in both themes.
 *   - resize grip: `brand-input`, the recessed input tint, so the 8px band
 *     reads as a groove at the panel's edge rather than a stripe; it lifts
 *     to the border tint on hover.
 *   - close button: `text-default`, the same ink as the panel title — white
 *     on the dark panel, near-black on the light one — with the input tint
 *     on hover.
 */
export const PortalSidebar: React.FC<PortalSidebarProps> = ({
	children,
	...props
}) => {
	const keyboardInset = useNativeIosKeyboardInset();

	const revealFocused = useCallback(
		(target: Element | null): void => {
			if (keyboardInset > 0 && target instanceof HTMLElement) {
				// After layout, and instant: WebKit's own focus handling cut a
				// smooth scroll short and left the field behind the keyboard.
				requestAnimationFrame((): void => {
					scrollAboveKeyboard(target, keyboardInset);
				});
			}
		},
		[keyboardInset],
	);

	// The keyboard reports its height just after the first field gains
	// focus, so reveal that field once the height arrives as well.
	useEffect(() => {
		revealFocused(document.activeElement);
	}, [revealFocused]);

	return (
		<Sidebar
			{...props}
			panelClassName="bg-brand-surface"
			dividerClassName="border-brand-border"
			closeButtonClassName="text-default hover:bg-brand-input"
			resizeHandleClassName="bg-brand-input hover:bg-brand-border"
		>
			{/* PD-17168 — in the iOS app the panel keeps its full height and gains
			    keyboard-height space at the bottom of its content instead of
			    shrinking above the keyboard (the CRM shell's approach). iOS's
			    keyboard arrows only step to fields inside the visible part of a
			    scrolled panel, so a shrunken panel left Next dead whenever the
			    next field sat below its edge. The space lets every field scroll
			    clear of the keyboard, and the focused one is scrolled there. */}
			<div
				onFocus={(event): void => {
					revealFocused(event.target);
				}}
				style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
			>
				{children}
			</div>
		</Sidebar>
	);
};

/**
 * The keyboard height inside the iOS app, 0 everywhere else. A browser needs
 * nothing from here: the shared Sidebar already lifts itself out of the
 * keyboard there, because mobile Safari reports it through visualViewport.
 */
function useNativeIosKeyboardInset(): number {
	const inset = useKeyboardInset();

	return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
		? inset
		: 0;
}

/** Scroll the field's scroll container until the field clears the keyboard. */
function scrollAboveKeyboard(field: HTMLElement, keyboardInset: number): void {
	const margin = 16;
	const visibleBottom = window.innerHeight - keyboardInset - margin;
	const overlap = field.getBoundingClientRect().bottom - visibleBottom;

	if (overlap <= 0) {
		return;
	}

	for (
		let element = field.parentElement;
		element != null;
		element = element.parentElement
	) {
		const { overflowY } = window.getComputedStyle(element);

		if (
			(overflowY === 'auto' || overflowY === 'scroll') &&
			element.scrollHeight > element.clientHeight
		) {
			element.scrollTop += overlap;
			return;
		}
	}
}
